import type { MetaDescriptor } from 'react-router';
import { selectedContentFolderFilterIds } from '@/lib/content-folder-filter';
import { buildPageSeoDescriptors, resolvePageSeo } from '@/lib/page-seo';
import type { ContentRouteData } from './types';

function isChildFolderFilterActive(
    loaderData: Extract<ContentRouteData, { kind: 'folderListing' | 'newsListing' }>
): boolean {
    return (
        loaderData.folder.c_showCategoryFilter === true &&
        selectedContentFolderFilterIds(loaderData.refine, new Set(loaderData.subCategoryLinks.map((link) => link.id)))
            .length > 0
    );
}

/** Head descriptors for content URLs. Owned by the route `meta` export, not by a body `<title>`. */
export function buildContentRouteMeta(loaderData: ContentRouteData): MetaDescriptor[] {
    const siteName = loaderData.seoSiteName;
    if (!siteName) return [];

    if (loaderData.kind === 'folderListing' || loaderData.kind === 'newsListing') {
        const childFolderFilterActive = isChildFolderFilterActive(loaderData);
        const { title, description, rawTitle } = resolvePageSeo({
            pageMetaTags: childFolderFilterActive ? undefined : loaderData.criticalSearchResult.pageMetaTags,
            pageTitle: loaderData.folder.pageTitle,
            pageDescription: loaderData.folder.pageDescription,
            title: loaderData.folder.title,
            description: loaderData.folder.description || undefined,
        });
        return buildPageSeoDescriptors({
            title,
            rawTitle,
            description,
            siteName,
            robots: loaderData.currentPage > 1 || childFolderFilterActive ? 'noindex, follow' : 'index, follow',
            prevUrl: loaderData.paginationPrevUrl ?? undefined,
            nextUrl: loaderData.paginationNextUrl ?? undefined,
            openGraph: { type: 'website', url: loaderData.pageUrl },
        });
    }

    const titleFallback =
        loaderData.kind === 'locationPage' ? loaderData.page.name : loaderData.page.name || loaderData.pageId;
    const { title, description, rawTitle } = resolvePageSeo({
        pageTitle: loaderData.page.pageTitle,
        pageDescription: loaderData.page.pageDescription,
        title: titleFallback,
        description: loaderData.page.description || undefined,
    });

    return buildPageSeoDescriptors({
        title,
        rawTitle,
        description,
        siteName,
        openGraph: { type: 'website', url: loaderData.pageUrl },
    });
}
